import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AreaApi from "../../services/AreaApi";
import PlantApi from "../../services/PlantApi";
import ProductionUnitApi from "../../services/ProductionUnitApi";
import { useToast } from "../../context/ToastContext";
import IApiAreasResponse from "../../interfaces/IApiAreasResponse";

// Design System
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const EditFormArea: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessages } = useTranslation("messages");
  const { t: translateGeneral } = useTranslation("general");

  const [name, setName] = useState("");
  const [plantId, setPlantId] = useState("");
  const [productionUnitId, setProductionUnitId] = useState("");
  
  const [plants, setPlants] = useState<{ label: string; value: string }[]>([]);
  const [productionUnits, setProductionUnits] = useState<{ label: string; value: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, [id]);

  useEffect(() => {
    if (plantId) {
      fetchProductionUnits(plantId);
    } else {
      setProductionUnits([]);
    }
  }, [plantId]);

  const fetchInitialData = async (): Promise<void> => {
    setIsLoading(true);
    try {
      // 1. Fetch Plants for dropdown
      const pResp = await PlantApi.GetAllPlants();
      const pOptions = (pResp.data || []).map((p: any) => ({
        label: p.name,
        value: p.id,
      }));
      setPlants(pOptions.sort((a: any, b: any) => a.label.localeCompare(b.label)));

      // 2. Fetch Area data
      const areasResp = await AreaApi.GetAllAreas();
      const area = (areasResp.data || []).find((a: IApiAreasResponse) => a.id === id);

      if (area) {
        setName(area.name);
        setProductionUnitId(area.productionUnitId);
        // We need the plantId to load the production units for the selected plant
        // In IApiAreasResponse, the productionUnit object has the plantId
        if (area.productionUnit) {
          setPlantId(area.productionUnit.plantId);
        }
      } else {
        displayToast("error", translateForm("error"), translateGeneral("noRecordsFound"), false, 3000);
        navigate("/areas");
      }
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProductionUnits = async (pId: string): Promise<void> => {
    try {
      const response = await ProductionUnitApi.GetProductUnitByPlant(pId);
      const options = (response.data || []).map((u: any) => ({
        label: u.name,
        value: u.id,
      }));
      setProductionUnits(options.sort((a: any, b: any) => a.label.localeCompare(b.label)));
    } catch (error) {
      console.error("Erro ao buscar unidades de produção: ", error);
    }
  };

  const handleUpdate = async (): Promise<void> => {
    if (!name || !productionUnitId || !id) {
      displayToast("error", translateForm("error"), translateMessages("fillAllFields"), false, 3000);
      return;
    }

    setIsLoading(true);
    try {
      await AreaApi.UpdateArea(id, productionUnitId, name);
      displayToast("success", translateMessages("editSuccess"), "", false, 3000);
      navigate("/areas");
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormCard
      title={translateMessages("editArea")}
      icon="edit"
      onSubmit={handleUpdate}
      onCancel={() => setShowConfirmCancel(true)}
      submitButtonLabel={translateGeneral("save")}
      cancelButtonLabel={translateGeneral("cancel")}
      disabled={isLoading}
      numColumns={6}
    >
      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translateForm("name")}
          required
          value={name}
          onChange={(val: any) => setName(val)}
        />
      </div>
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translateForm("plant")}
          required
          value={plantId}
          options={plants}
          optionLabel="label"
          optionValue="value"
          onChange={(val: any) => {
            setPlantId(val);
            setProductionUnitId("");
          }}
        />
      </div>
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translateForm("productionUnit")}
          required
          disabled={!plantId}
          value={productionUnitId}
          options={productionUnits}
          optionLabel="label"
          optionValue="value"
          onChange={(val: any) => setProductionUnitId(val)}
        />
      </div>

      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/areas")}
      />
    </FormCard>
  );
};

export default EditFormArea;
